import { useRequiredContext } from "@redotech/react-util/context";
import { useTranslation } from "@redotech/redo-i18n";
import PlusIcon from "@redotech/redo-icons/plus.svg";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import type { AdvancedFilterData } from "@redotech/redo-views-common/advanced-filters/advanced-filter-data";
import { CREATE_VIEW_NAME } from "@redotech/redo-views-common/views";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoHorizontalTabs } from "@redotech/redo-web/arbiter-components/tabs/redo-horizontal-tabs";
import {
  type PageHeaderSurface,
  usePageBreadcrumbs,
  usePageHeaderSurface,
  usePageTitle,
} from "@redotech/redo-web/page-header-slot";
import { TabsPortal } from "@redotech/redo-web/tabs-portal";
import { RedoHorizontalTabsWithActions } from "@redotech/redo-web/tabs-with-actions";
import { memo, useContext, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  getReturnListViewLabel,
  isReturnListSystemViewIdentity,
} from "../returns-list-labels";
import {
  RecordViewCatalogSource,
  useRecordViewCatalog,
} from "../views/use-record-view-catalog";
import { RecordViewTabsContext } from "./record-view-tabs-context";
import {
  getHiddenViewName,
  selectedViewTab,
  ViewTabLink,
} from "./selected-view-tab";

const NO_ALIASES: readonly string[] = [];
const TABLE_HEADER_SURFACE: PageHeaderSurface = {
  divider: true,
  bgColor: "primary",
  breadcrumbSize: "sm",
  breadcrumbWeight: "regular",
  // The trail reads Home > Returns under the "Returns" title, as in the prototype.
  keepTrailingBreadcrumb: true,
};
const NO_TABS: readonly ViewTab[] = [];

function isHiddenSystemView(viewName: string): boolean {
  return (
    viewName !== CREATE_VIEW_NAME && isReturnListSystemViewIdentity(viewName)
  );
}

export interface ViewTab extends ViewTabLink {
  label: string;
  count?: number;
  /** A built-in tab's filters, so the table knows they aren't the user's change. */
  filters?: AdvancedFilterData[];
  /** Built-in tabs can't be edited or deleted from the table. */
  builtIn?: boolean;
}

/**
 * One record type's views as tabs under the page title: All, built-ins and
 * saved views from the catalog, then the type's other tables.
 */
export const RecordViewTabs = memo(function RecordViewTabs({
  source,
  allAliases = NO_ALIASES,
  otherTabs = NO_TABS,
  title,
  section,
  canCreateView = false,
}: {
  source?: RecordViewCatalogSource;
  /** Paths besides the All view's that show every record, such as the index. */
  allAliases?: readonly string[];
  otherTabs?: readonly ViewTab[];
  title: string;
  /** The breadcrumb after Home; the title when unset. */
  section?: string;
  /** Shows the + that asks the table to save its current filters as a view. */
  canCreateView?: boolean;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const tabsChannel = useContext(RecordViewTabsContext);
  const [group] = useRecordViewCatalog(source ? [source] : []);

  const tabs = useMemo(
    (): ViewTab[] => [
      ...(group?.views ?? []).map((view, index) => ({
        key: view.key,
        href: view.href,
        label: view.label,
        count: view.count,
        filters: view.filters,
        builtIn: view.builtIn,
        aliases: index === 0 ? allAliases : undefined,
      })),
      ...otherTabs,
    ],
    [group, allAliases, otherTabs],
  );
  const selected = selectedViewTab(tabs, pathname, search);
  const allHref = group?.views[0]?.href;
  const hiddenView =
    !selected && allHref
      ? getHiddenViewName(allHref, pathname, isHiddenSystemView)
      : undefined;
  // A view's table, including a bookmarked hidden one, listens for the create
  // request; sibling pages (registrations, instant exchange...) don't.
  const onViewTable = selected
    ? !otherTabs.some((tab) => tab.key === selected.key)
    : hiddenView !== undefined;
  // A system view reached by bookmark counts as built-in too.
  const selectedTabFilters = selected?.filters;
  const selectedTabLocked = selected
    ? !!selected.builtIn
    : hiddenView !== undefined;
  const setSelectedTab = tabsChannel?.setSelectedTab;
  useEffect(() => {
    setSelectedTab?.({
      filters: selectedTabFilters,
      locked: selectedTabLocked,
    });
    return () => setSelectedTab?.(undefined);
  }, [selectedTabFilters, selectedTabLocked, setSelectedTab]);
  const viewLabel =
    selected?.label ??
    (hiddenView !== undefined ? getReturnListViewLabel(hiddenView) : undefined);

  if (viewLabel === undefined) {
    return null;
  }

  const hrefByKey = new Map(tabs.map((tab) => [tab.key, tab.href]));
  return (
    <>
      {/* The trail names the record type, never the selected view. */}
      <RecordPageHeader crumb={section ?? title} title={title} />
      <TabsPortal>
        <RedoHorizontalTabsWithActions
          actions={
            canCreateView &&
            onViewTable && (
              <RedoButton
                aria-label={t("Create view")}
                data-dd-action-name="record_view_tabs:create_view_click"
                dataTestId="record-view-tabs-create-view"
                hierarchy="tertiary"
                IconLeading={PlusIcon}
                onClick={() => tabsChannel?.requestCreate()}
                size="xs"
              />
            )
          }
          align="flex-end"
          gap="xs"
          justify="flex-start"
          stretchTabs={false}
          tabs={
            // Natural width so the + sits right after the last tab; the row
            // has room, so tabs never fold into a More menu.
            <RedoHorizontalTabs
              drawBottomLine={false}
              pl="none"
              preventMoreTab
              selectedTab={selected?.key}
              setSelectedTab={(key) => {
                const href = hrefByKey.get(key);
                if (href) {
                  navigate(href);
                }
              }}
              tabs={tabs.map((tab) => ({
                key: tab.key,
                label: tab.label,
                count: tab.count,
                dataTestId: `record-view-tab-${tab.key}`,
              }))}
            />
          }
        />
      </TabsPortal>
    </>
  );
});

/** The new experience's page header: title, Home > crumb trail and surface. */
export const RecordPageHeader = memo(function RecordPageHeader({
  title,
  crumb,
}: {
  title: string;
  crumb: string;
}) {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);
  usePageTitle(title);
  // The same 14px trail as the return details header.
  usePageHeaderSurface(TABLE_HEADER_SURFACE);
  const breadcrumbs = useMemo(
    () => [
      { id: "home", name: t("Home"), url: routes.returnsHref },
      { id: "view", name: crumb, url: null },
    ],
    [crumb, t, routes.returnsHref],
  );
  usePageBreadcrumbs(breadcrumbs);
  return null;
});
